import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { NotPermitted } from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { oversightApi } from '../api/oversight-api';
import type { IReviewFlag } from '../api/oversight-contracts';
import { LoadError, TableSkeleton, useAdminData } from '../components/admin-page';
import { refusalMessage } from '../refusal';
import { useResponseContext } from './response-view-page';

/**
 * Review flags — the internal channel, never respondent-visible, and the closest thing this
 * surface has to "findings". It is not the same thing and must not be presented as one: a
 * finding is the rule engine's verdict on a live answer, a flag is a durable note that
 * something needs a person to look at it. The engine raises its own (`source: rule`); this
 * screen raises `manual` ones, and the server sets that source itself so a client cannot
 * claim the engine's dedup namespace.
 *
 * The write half is the first thing in this app that changes anything on someone else's
 * response, so two of the API's rules are enforced up front rather than discovered by a
 * round trip: a flag is addressed at a usage the response's **pinned version** actually
 * carries — hence a picker over that version rather than a free-text field, which would
 * turn a typo into a 422 — and only an `open` flag can be resolved or dismissed, so the
 * buttons disappear once it is neither.
 *
 * Manual flags are deliberately not deduplicated by the API: two people noticing the same
 * field are two observations, not one.
 *
 * Raising, resolving and dismissing are `responses.annotate` (SR-228); without it the form and
 * both buttons are drawn off under one `NotPermitted` sentence, and the flags are still read.
 */
export function AdminResponseFlagsPage() {
  const { responseId = '' } = useParams();
  const { response, reload: reloadResponse } = useResponseContext();

  const {
    data: flags,
    error,
    setError,
    reload
  } = useAdminData<IReviewFlag[]>((signal) => oversightApi.flags(responseId, signal), [responseId]);

  const [busy, setBusy] = React.useState(false);
  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();
  const notPermitted = permitted ? undefined : reasonId;

  /** Both halves re-read: a raise or a transition moves `openFlags` on the card above. */
  const afterWrite = async () => {
    await Promise.all([reload(), reloadResponse()]);
  };

  const act = async (action: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await afterWrite();
    } catch (writeError) {
      setError(refusalMessage(writeError, fallback));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && <LoadError message={error} />}

      {!permitted && (
        <NotPermitted id={reasonId} what="raising, resolving or dismissing review flags" />
      )}
      <RaiseFlag
        busy={busy}
        notPermitted={notPermitted}
        usageCodes={response.sections.flatMap((section) =>
          section.usages.map((usage) => usage.code)
        )}
        onRaise={(body) =>
          act(() => oversightApi.raiseFlag(responseId, body), 'Could not raise the flag')
        }
      />

      {flags === null ? (
        <TableSkeleton rows={3} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-28">Source</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Message</TableHead>
                <TableHead className="w-40">Field</TableHead>
                <TableHead className="w-44">Raised</TableHead>
                <TableHead className="w-44" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {flags.map((flag) => (
                <TableRow key={flag.flagId}>
                  <TableCell>
                    <Badge variant={flag.status === 'open' ? 'destructive' : 'outline'}>
                      {flag.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{flag.source}</TableCell>
                  <TableCell className="font-mono text-xs">{flag.code}</TableCell>
                  <TableCell className="text-sm">{flag.message}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {flag.usage ?? 'whole response'}
                    {flag.instanceKey !== '' && (
                      <span className="block font-sans text-[11px] text-muted-foreground">
                        {flag.instanceKey}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDateTime(flag.raisedAt)}
                    {flag.resolvedAt && (
                      <span className="block">closed {formatDateTime(flag.resolvedAt)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {flag.status === 'open' && (
                      <span className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={busy || !permitted}
                          aria-describedby={notPermitted}
                          onClick={() =>
                            act(
                              () =>
                                oversightApi.setFlagStatus(responseId, flag.flagId, 'resolved'),
                              'Could not resolve the flag'
                            )
                          }
                        >
                          Resolve
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={busy || !permitted}
                          aria-describedby={notPermitted}
                          onClick={() =>
                            act(
                              () =>
                                oversightApi.setFlagStatus(responseId, flag.flagId, 'dismissed'),
                              'Could not dismiss the flag'
                            )
                          }
                        >
                          Dismiss
                        </Button>
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {flags.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                    No flag on this response.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

/**
 * The raise form. `code` is a short slug the API validates against the model's own code
 * pattern, so it stays a text field rather than a picker over a vocabulary that does not
 * exist: manual flag codes are whatever a reviewing team agrees on, and inventing a closed
 * list here would be this screen designing one.
 */
function RaiseFlag({
  busy,
  notPermitted,
  usageCodes,
  onRaise
}: {
  busy: boolean;
  /** The id of the page's `NotPermitted` sentence; present, the whole form is drawn off. */
  notPermitted?: string;
  usageCodes: string[];
  onRaise: (body: { code: string; message: string; usage?: string }) => Promise<void>;
}) {
  const [code, setCode] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [usage, setUsage] = React.useState(WHOLE_RESPONSE);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await onRaise({
      code: code.trim(),
      message: message.trim(),
      usage: usage === WHOLE_RESPONSE ? undefined : usage
    });
    setCode('');
    setMessage('');
    setUsage(WHOLE_RESPONSE);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Raise a flag</CardTitle>
        <CardDescription>
          Internal to staff — a respondent never sees a flag. Leave the field unset to flag
          the response as a whole.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-3" onSubmit={submit}>
          {/* A disabled fieldset turns off every native control in it — but not a Radix select,
              which opens on pointerdown whatever its trigger's state: that one is told itself. */}
          <fieldset disabled={notPermitted !== undefined} className="contents">
            <div className="flex flex-wrap gap-3">
              <Input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="Code, e.g. aum_looks_wrong"
                aria-label="Flag code"
                aria-describedby={notPermitted}
                className="max-w-xs"
              />
              <Select
                value={usage}
                onValueChange={setUsage}
                disabled={notPermitted !== undefined}
              >
                <SelectTrigger
                  className="w-64"
                  aria-label="Field this flag is about"
                  aria-describedby={notPermitted}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={WHOLE_RESPONSE}>The whole response</SelectItem>
                  {usageCodes.map((usageCode) => (
                    <SelectItem key={usageCode} value={usageCode}>
                      {usageCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="What needs looking at, and why"
              aria-label="Flag message"
              aria-describedby={notPermitted}
              rows={2}
            />
            <span className="flex justify-end">
              <Button
                type="submit"
                disabled={busy || code.trim() === '' || message.trim() === ''}
                aria-describedby={notPermitted}
              >
                Raise flag
              </Button>
            </span>
          </fieldset>
        </form>
      </CardContent>
    </Card>
  );
}

/** A flag with no usage is a response-level one; `Select` needs a value to say that with. */
const WHOLE_RESPONSE = 'whole-response';
